/**
 * Async enterprise search for tutor professional-situation fields.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { searchEnterprises } from '@/services/enterprises.service';
import type { SelectOption } from '@/components/forms/Select';

/** Typeahead over `GET /employers/search`. */
export const useEnterprisesAutocomplete = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['enterprises-search', searchQuery, page],
    queryFn: async () => {
      const result = await searchEnterprises(searchQuery, 10);
      // Simulate pagination on client since backend doesn't support it yet
      const start = (page - 1) * 10;
      const end = page * 10;
      return {
        data: result.slice(start, end),
        hasMore: end < result.length,
      };
    },
    enabled: searchQuery.length >= 2,
  });

  /** Accumulate pages as they load. */
  useMemo(() => {
    if (!data?.data) return;

    if (page === 1) {
      setAccumulatedData(data.data);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((e: any) => e.id));
        const uniqueNew = data.data.filter((e: any) => !existingIds.has(e.id));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
    setAccumulatedData([]);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore) {
      setPage((p) => p + 1);
    }
  }, [data]);

  const options: SelectOption[] = accumulatedData.map((enterprise) => ({
    value: enterprise.id,
    label: enterprise.name,
  }));

  return {
    options,
    isLoading,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    hasMore: data?.hasMore || false,
  };
};
